import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

// Duplicate-save guard: an exact repeat of the same Sales/Expenses submit (double tap, network retry,
// reconnect) reuses one X-Idempotency-Key, so the server saves it once. A new entry gets a new key.
const IDEM_PATHS = ['/sales', '/expenses'];
const idemKeys = new Map();
const idemSig = (config) => {
  const path = (config.url || '').split('?')[0].replace(/\/+$/, '');
  if ((config.method || '').toLowerCase() !== 'post' || !IDEM_PATHS.includes(path)) return null;
  return `${path}|${typeof config.data === 'string' ? config.data : JSON.stringify(config.data || {})}`;
};
const attachIdemKey = (config) => {
  const sig = idemSig(config);
  if (!sig) return;
  const now = Date.now();
  for (const [k, v] of idemKeys) if (v.until && v.until < now) idemKeys.delete(k);
  let e = idemKeys.get(sig);
  if (!e) {
    e = { key: (window.crypto?.randomUUID?.() || `${now}-${Math.random().toString(36).slice(2)}`), until: now + 120000 };
    idemKeys.set(sig, e);
  }
  config.headers = config.headers || {};
  config.headers['X-Idempotency-Key'] = e.key;
  config.__idemSig = sig;
};
const settleIdemKey = (config, ok) => {
  const e = config && idemKeys.get(config.__idemSig);
  if (!e) return;
  if (ok) e.until = Date.now() + 5000; // a tap that lands just after the save still dedupes
  else idemKeys.delete(config.__idemSig); // server refused: a corrected resubmit is a new submit
};

api.interceptors.request.use((config) => {
  attachIdemKey(config);
  // Token routing (iter — Phase A.1):
  //  • Most of the ERP uses the full-ERP JWT in localStorage('token').
  //  • A PIN-logged POS session has a cashier_token (a real user JWT minted
  //    by /cashier/login). On the endpoints a POS session legitimately needs,
  //    the LIVE cashier_token must take precedence over a possibly-stale
  //    full-ERP token so the request authenticates as the current cashier.
  //    Backend get_current_user still enforces that user's OWN permissions —
  //    this grants NO extra backend rights, it only picks which existing
  //    bearer to send. We do NOT attach cashier creds to arbitrary endpoints.
  const url = (config.url || '').toLowerCase();
  const posSessionPath =
    url.includes('/cashier/') ||
    url.includes('/cashier-') ||
    url.includes('/zatca-p2/status') ||
    url.includes('/pos/') ||
    url.includes('/customer-display/') ||
    url.includes('/upsells/suggestions') ||
    url.includes('/branches') ||
    // Phase B — the shared Sales Reconciliation dialog hits /sales from inside
    // the POS. In a PIN-only session the LIVE cashier_token (the user's own
    // JWT, carrying their own permissions) must win over a possibly-stale
    // full-ERP token. Backend require_permission('sales') remains authoritative
    // — this only picks which existing bearer to send, no extra rights.
    url === '/sales' ||
    url.startsWith('/sales?') ||
    url.startsWith('/sales/') ||
    // Phase-B POS blocker fix — the SplitPaymentDialog loads bank accounts and
    // payment terminals from inside the POS. In a PIN-only session the LIVE
    // cashier_token must win so these return the branch's accounts/terminals
    // (otherwise Bank payments record with no mapped account). Backend auth
    // stays authoritative; this only picks which existing bearer to send.
    url.includes('/bank-accounts') ||
    url.includes('/payment-terminals');
  const fullToken = localStorage.getItem('token');
  const cashierToken = localStorage.getItem('cashier_token');
  // Only let the cashier_token OVERRIDE a full-ERP token while the user is
  // actually inside the POS UI (/cashier…). Elsewhere the full-ERP token
  // wins exactly as before; cashier_token is only a fallback when no
  // full-ERP token exists on a POS-session path. This avoids any regression
  // for an admin who merely left a cashier_token behind in the ERP.
  const inPosUi = typeof window !== 'undefined' && /^\/cashier/.test(window.location?.pathname || '');
  // iter — /branches is the ONE POS-session path where the cashier_token must
  // NOT override a present full-ERP token: the POS "Request Stock from Branch"
  // picker needs the operator's FULL allowed-branch list (siblings to request
  // from). The branch-scoped cashier_token returns only the till's own branch
  // (or none), which emptied the source-branch dropdown. The full token still
  // only grants that user's own backend permissions. In a pure-PIN session
  // (no full token) cashier_token remains the fallback so /branches still auths.
  const branchesPath = url.includes('/branches');
  const token = (posSessionPath && inPosUi && cashierToken && !branchesPath)
    ? cashierToken
    : (fullToken || (posSessionPath ? cashierToken : null));
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry idempotent GET requests on cold-start / transient upstream errors
// (502/503/504 bad gateway, or ERR_NETWORK / connection-refused). This eliminates
// the ~1-second window during K8s pod cold-start where nginx returns the proxy
// error before uvicorn has bound port 8001.
const isColdStartError = (error) => {
  const status = error.response?.status;
  if (status && [502, 503, 504].includes(status)) return true;
  // axios sets error.code === 'ERR_NETWORK' on TCP-level refusal
  if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
    return true;
  }
  return false;
};

api.interceptors.response.use(
  (response) => { settleIdemKey(response.config, true); return response; },
  async (error) => {
    if (error?.response) settleIdemKey(error.config, false); // no response (network) → keep key for the retry
    // Normalize Pydantic 422 `detail` array → human-readable string.
    // Many callers do `toast.error(err.response?.data?.detail || 'Failed')`
    // which crashed React when `detail` was an array of `{type,loc,msg,...}`
    // objects. We mutate the response in place so every caller sees a string.
    try {
      const d = error?.response?.data?.detail;
      if (error?.response?.status === 503 && d === 'Storage unavailable. Please retry later.') {
        import('sonner').then(({ toast }) => toast.error(d, { id: 'storage-unavailable' }));
      }
      if (Array.isArray(d)) {
        error.response.data.detail = d.map((it) => {
          if (typeof it === 'string') return it;
          if (it && typeof it === 'object') {
            const loc = Array.isArray(it.loc) ? it.loc.filter(x => x !== 'body').join('.') : '';
            const msg = it.msg || it.message || JSON.stringify(it);
            return loc ? `${loc}: ${msg}` : msg;
          }
          return String(it);
        }).join('; ');
      } else if (d && typeof d === 'object') {
        error.response.data.detail = d.msg || d.message || JSON.stringify(d);
      }
    } catch (_) { /* never let normaliser break the rejection chain */ }

    // Real-time journal sync: cancelling a POSTED transaction needs a reason → ask once and retry.
    const cfg = error?.config;
    const rd = error?.response?.data?.detail;
    if (error.response?.status === 400 && cfg && !cfg._reasonAsked && typeof rd === 'string' && rd.startsWith('A reason is required')) {
      const reason = window.prompt(`${rd}.\nThe posted journal will be reversed (original kept for audit). Reason:`);
      if (reason && reason.trim()) {
        cfg._reasonAsked = true;
        if ((cfg.method || '').toLowerCase() === 'delete') cfg.params = { ...(cfg.params || {}), reason: reason.trim() };
        else cfg.data = JSON.stringify({ ...(typeof cfg.data === 'string' ? JSON.parse(cfg.data || '{}') : (cfg.data || {})), reason: reason.trim() });
        return api.request(cfg);
      }
    }

    if (error.response?.status === 401) {
      const detail = (error.response?.data?.detail || '').toLowerCase();
      const tokenIssue =
        detail.includes('credential') ||
        detail.includes('token') ||
        detail.includes('user not found') ||
        detail.includes('expired') ||
        detail.includes('not authenticated') ||  // iter611 — generic FastAPI default
        detail.includes('unauthorized');
      if (tokenIssue) {
        // iter611 — Differentiate cashier-side 401s from admin-side
        // ones: a cashier session expiring during the iPad's lunch
        // rush should kick them back to /cashier (PIN keypad), not
        // /login (admin form). Detect by URL prefix.
        const url = (error.config?.url || '').toLowerCase();
        const isCashierPath = url.includes('/cashier/') || url.includes('/cashier-');
        if (isCashierPath) {
          localStorage.removeItem('cashier_token');
          window.location.href = '/cashier';
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
    // Silent retry for cold-start blips — only on idempotent GETs, max 2 retries.
    const config = error.config || {};
    const method = (config.method || 'get').toLowerCase();
    if (method === 'get' && isColdStartError(error)) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= 2) {
        const delay = 600 * config.__retryCount; // 600ms, 1200ms
        await new Promise((r) => setTimeout(r, delay));
        return api(config);
      }
    }
    return Promise.reject(error);
  },
);

export default api;
